import { useEffect, useRef, useState, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'

import { UploadSelfie as BLUploadSelfie } from './../../../utilities/BL/SelfieFan'

import { useFloatMessage, MESSAGE_TYPE } from './../../Providers/FloatMessage'


// -----------------------------------------------------------------------------
// SelfieFan.jsx
// Componente para capturar una selfie desde la cámara, previsualizar, rotar,
// y subirla con metadata { source: "selfie_fan" }.
// Incluye "cooldown" para permitir otra subida hasta pasados X ms.
// -----------------------------------------------------------------------------

// Clave para persistir el cooldown entre recargas
const COOLDOWN_STORAGE_KEY = "selfiefan_next_allowed_ts";

const LIVE_PRIVACY_TEXT = `AVISO DE PRIVACIDAD SIMPLIFICADO - CLUB ATLÉTICO DE SAN LUIS

En estricto cumplimiento con la Ley Federal de Protección de Datos Personales en Posesión de los Particulares (LFPDPPP), sus datos personales (Nombre completo, Correo electrónico y Teléfono celular) serán tratados por el Club Atlético de San Luis, con el soporte tecnológico de Five Digital Minds e Interaktia, para las siguientes finalidades:

1. Validar su identidad y participación en la dinámica "Live Barrio 9".
2. Contactar a los participantes seleccionados para gestionar la entrega de premios oficiales del club.
3. Atender dudas, aclaraciones y dar seguimiento a dinámicas y beneficios oficiales del club.

Sus datos personales están resguardados con estrictas medidas de seguridad técnicas, físicas y administrativas, y no serán transferidos ni comercializados con terceros ajenos a esta dinámica. Al registrarse y marcar la casilla correspondiente, usted autoriza el tratamiento de sus datos conforme al presente aviso. Para más información o para el ejercicio de sus derechos ARCO, consulte los canales y el portal oficial del Club Atlético de San Luis.`;

const LIVE_TERMS_TEXT = `TÉRMINOS Y CONDICIONES - DINÁMICA LIVE BARRIO 9

1. Organizador: El Club Atlético de San Luis es el único responsable y organizador de la dinámica "Live Barrio 9", así como de la selección de ganadores y entrega de los premios o beneficios correspondientes.

2. Proveedor Tecnológico y Deslinde: Five Digital Minds e Interaktia actúan exclusivamente como proveedores de la infraestructura y plataforma tecnológica digital, por lo que quedan totalmente eximidas de cualquier responsabilidad relacionada con la logística del evento, la asignación, validez o entrega física de premios.

3. Participación: Dinámica abierta a los aficionados que completen correctamente su registro con datos verídicos y capturen su fotografía a través de esta plataforma digital oficial.

4. Autorización de Imagen: Al capturar y enviar su fotografía, el participante autoriza de manera voluntaria y no exclusiva al Club Atlético de San Luis a proyectar su selfie en las pantallas del estadio, circuito cerrado y/o plataformas digitales oficiales con fines de entretenimiento durante el evento.

5. Premiación y Contacto: Los ganadores serán determinados y contactados exclusivamente por el comité organizador del Club Atlético de San Luis utilizando los medios de contacto proporcionados en este registro (teléfono y/o correo).

6. Descalificación: El uso de identidades falsas, datos de contacto inválidos o el envío de fotografías que vulneren las normas de convivencia, respeto o moral pública resultará en la descalificación inmediata sin derecho a reclamo.

7. Decisiones: Las resoluciones del comité organizador del Club Atlético de San Luis respecto a cualquier aspecto de la dinámica y premiación son definitivas e inapelables.`;

// Utilidad: Formatea ms → "m:ss"
function msToMinSec(ms) {
  const total = Math.ceil(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

/**
 * Espera a que el <video> tenga dimensiones reales (videoWidth/Height > 0).
 * Usa eventos + polling + timeout por seguridad.
 */
function waitForVideoReady(video, timeoutMs = 8000) {
  return new Promise((resolve, reject) => {
    if (!video) return reject(new Error("Referencia <video> nula"));

    if (video.videoWidth > 0 && video.videoHeight > 0) {
      return resolve();
    }

    let done = false;

    const onReady = () => {
      if (done) return;
      if (video.videoWidth > 0 && video.videoHeight > 0) {
        done = true;
        cleanup();
        resolve();
      }
    };

    const onLoadedMetadata = () => onReady();
    const onCanPlay = () => onReady();

    const poll = () => {
      if (done) return;
      if (video.videoWidth > 0 && video.videoHeight > 0) {
        onReady();
      } else {
        requestAnimationFrame(poll);
      }
    };

    const timer = setTimeout(() => {
      if (done) return;
      done = true;
      cleanup();
      reject(new Error("Timeout esperando a que la cámara esté lista"));
    }, timeoutMs);

    const cleanup = () => {
      clearTimeout(timer);
      video.removeEventListener("loadedmetadata", onLoadedMetadata);
      video.removeEventListener("canplay", onCanPlay);
    };

    video.addEventListener("loadedmetadata", onLoadedMetadata);
    video.addEventListener("canplay", onCanPlay);
    poll();
  });
}

/**
 * Componente SelfieFan
 *
 * Vistas:
 * - 'intro'   : instrucciones + botón "Iniciar cámara"
 * - 'camera'  : cámara a pantalla completa + botón "Tomar foto"
 * - 'preview' : vista previa + girar/restaurar + elegir mensaje + enviar
 * - 'sent'    : confirmación + cooldown con contador
 */
const SelfieFan = ({ cooldownMs = 2 * 60 * 1000 /* default 2 minutos */ }) => {
  const { showMessage } = useFloatMessage();
  const [searchParams] = useSearchParams();
  const isLiveMode = searchParams.get('s') === 'l';

  // --- Estados para Live Barrio 9 (Formulario de contacto - siempre limpio por participación) ---
  const [leadData, setLeadData] = useState({ fullName: '', email: '', phone: '', acceptedTerms: false });

  // Limpieza defensiva de cualquier dato previo que haya quedado en localStorage
  useEffect(() => {
    try {
      localStorage.removeItem("selfie_live_lead_data");
    } catch {}
  }, []);
  const [leadErrors, setLeadErrors] = useState({});
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  // --- Refs DOM ---
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const previewUrlRef = useRef(null);

  // --- Control de vistas ---
  const [view, setView] = useState("intro"); // 'intro' | 'camera' | 'preview' | 'sent'

  // --- Estados de cámara/subida ---
  const mediaStreamRef = useRef(null);
  const [isStarting, setIsStarting] = useState(false);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  // --- Imagen/preview/rotación ---
  const [previewUrl, setPreviewUrl] = useState(null);
  const [lastBlob, setLastBlob] = useState(null); // lo que realmente se sube
  const [baseBlob, setBaseBlob] = useState(null); // blob base para giros
  const baseBitmapRef = useRef(null); // ImageBitmap o HTMLImageElement
  const [rotation, setRotation] = useState(0); // 0 | 90 | 180 | 270

  // --- Mensajes de apoyo ---
  const supportMessages = { ONE: 1, TWO: 2, THREE: 3 };
  const SUPPORT_MESSAGE_BY_ID = {
    [supportMessages.ONE]: "Vamos con todo equipo",
    [supportMessages.TWO]: "Viva el San Luis",
    [supportMessages.THREE]: "Arriba las Auriazules",
  };
  const DEFAULT_SUPPORT_MESSAGE = SUPPORT_MESSAGE_BY_ID[supportMessages.ONE];
  const [supportMessageSelected, setSupportMessageSelected] = useState(0);

  // --- Límites de tamaño para controlar el archivo ---
  const MAX_WIDTH = 1280;
  const MAX_HEIGHT = 1280;

  // --- Cooldown (próxima vez permitida) ---
  const [nextAllowedTs, setNextAllowedTs] = useState(null);
  const [remainingMs, setRemainingMs] = useState(0);

  // Cargar cooldown persistido al montar
  useEffect(() => {
    const saved = localStorage.getItem(COOLDOWN_STORAGE_KEY);
    if (saved) {
      const ts = parseInt(saved, 10);
      if (!Number.isNaN(ts)) setNextAllowedTs(ts);
    }
  }, []);

  // Tick de countdown
  useEffect(() => {
    const tick = () => {
      if (!nextAllowedTs) {
        setRemainingMs(0);
        return;
      }
      const diff = nextAllowedTs - Date.now();
      setRemainingMs(Math.max(0, diff));
    };

    tick();
    const id = setInterval(tick, 500);
    return () => clearInterval(id);
  }, [nextAllowedTs]);

  const canStartNew = !nextAllowedTs || Date.now() >= nextAllowedTs;

  // --- Helpers de imagen ---
  async function ensureBaseBitmapFromBlob(blob) {
    if (!blob) return;
    try {
      if ("createImageBitmap" in window) {
        baseBitmapRef.current = await createImageBitmap(blob);
        return;
      }
    } catch {
      // fallback continúa
    }

    const objUrl = URL.createObjectURL(blob);
    await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        baseBitmapRef.current = img;
        URL.revokeObjectURL(objUrl);
        resolve();
      };
      img.onerror = (e) => {
        URL.revokeObjectURL(objUrl);
        reject(e);
      };
      img.src = objUrl;
    });
  }

  /**
   * Renderiza desde el bitmap base → canvas aplicando la rotación,
   * actualiza el preview y el blob final (lastBlob).
   */
  async function renderFromBaseAndUpdate(rotationDeg) {
    const bmp = baseBitmapRef.current;
    if (!bmp || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    const srcW = bmp.width;
    const srcH = bmp.height;

    const isQuarterTurn = rotationDeg % 180 !== 0;
    const outW = isQuarterTurn ? srcH : srcW;
    const outH = isQuarterTurn ? srcW : srcH;

    canvas.width = outW;
    canvas.height = outH;

    ctx.save();
    switch (rotationDeg % 360) {
      case 0:
        break;
      case 90:
        ctx.translate(outW, 0);
        ctx.rotate(Math.PI / 2);
        break;
      case 180:
        ctx.translate(outW, outH);
        ctx.rotate(Math.PI);
        break;
      case 270:
        ctx.translate(0, outH);
        ctx.rotate((3 * Math.PI) / 2);
        break;
      default:
        break;
    }

    ctx.drawImage(bmp, 0, 0);
    ctx.restore();

    const rotatedBlob = await new Promise((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error("No se pudo generar la imagen rotada"))),
        "image/jpeg",
        0.92
      );
    });

    const objUrl = URL.createObjectURL(rotatedBlob);
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    previewUrlRef.current = objUrl;
    setPreviewUrl(objUrl);
    setLastBlob(rotatedBlob);
  }

  // --- Cámara: iniciar / detener ---
  const validateLeadForm = () => {
    if (!isLiveMode) return true;
    const errors = {};
    if (!leadData.fullName || leadData.fullName.trim().length < 3) {
      errors.fullName = "Ingresa tu nombre completo (mínimo 3 caracteres).";
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!leadData.email || !emailRegex.test(leadData.email.trim())) {
      errors.email = "Ingresa un correo electrónico válido.";
    }
    const phoneDigits = (leadData.phone || '').replace(/\D/g, "");
    if (phoneDigits.length !== 10) {
      errors.phone = "Ingresa tu número celular a 10 dígitos.";
    }
    if (!leadData.acceptedTerms) {
      errors.acceptedTerms = "Debes aceptar los Términos y el Aviso de Privacidad.";
    }
    setLeadErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleStartCameraWithValidation = () => {
    if (isLiveMode) {
      if (!validateLeadForm()) {
        showMessage({
          type: MESSAGE_TYPE.BAD,
          text: "Por favor completa tus datos correctamente para continuar.",
        });
        return;
      }
    }
    startCamera();
  };

  const startCamera = useCallback(async () => {
    // Si sigue en cooldown, no dejamos iniciar
    if (!canStartNew) {
      showMessage({
        type: MESSAGE_TYPE.INFO,
        text: `Podrás subir otra foto en ${msToMinSec(nextAllowedTs - Date.now())}.`,
      });
      return;
    }

    setErrorMsg(null);
    setIsStarting(true);
    setIsVideoReady(false);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "user" } },
        audio: false,
      });
      mediaStreamRef.current = stream;

      // Primero montamos la vista cámara para que el <video> exista en el DOM
      setView("camera");
    } catch (err) {
      console.error("Error al acceder a la cámara:", err);
      showMessage({
        type: MESSAGE_TYPE.BAD,
        text:
          "¡Ups! No ha sido posible acceder a la cámara. Revisa y autoriza los permisos.",
      });
      setErrorMsg("No ha sido posible acceder a la cámara: " + err.message);
    } finally {
      setIsStarting(false);
    }
  }, [canStartNew, nextAllowedTs, showMessage]);

  const stopCamera = useCallback(() => {
    const stream = mediaStreamRef.current;
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
      mediaStreamRef.current = null;
    }
  }, []);

  // Cuando la vista es "camera", conectamos el stream al <video> y esperamos a que esté listo
  useEffect(() => {
    if (view !== "camera") return;

    const v = videoRef.current;
    const stream = mediaStreamRef.current;
    if (!v || !stream) return;

    v.srcObject = stream;
    v.muted = true;
    v.setAttribute("playsinline", "true");

    let cancelled = false;

    (async () => {
      try {
        try {
          await v.play();
        } catch {
          // algunos navegadores requieren interacción del usuario; el botón se mantendrá deshabilitado hasta ready
        }
        await waitForVideoReady(v, 8000);
        if (!cancelled) setIsVideoReady(true);
      } catch (e) {
        if (cancelled) return;
        setIsVideoReady(false);
        setErrorMsg("No se pudo preparar el video: " + (e?.message ?? ""));
        showMessage({
          type: MESSAGE_TYPE.BAD,
          text: "No se pudo preparar la cámara. Intenta de nuevo.",
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [view, showMessage]);

  // Cleanup global al desmontar
  useEffect(() => {
    return () => {
      stopCamera();
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
    };
  }, [stopCamera]);

  // --- Capturar frame del video a canvas ---
  const handleCapture = useCallback(async () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    // Si por alguna razón aún no está listo, esperamos un poco más
    if (video.videoWidth === 0 || video.videoHeight === 0) {
      try {
        await waitForVideoReady(video, 3000);
      } catch {
        showMessage({
          type: MESSAGE_TYPE.BAD,
          text: "La cámara aún no está lista. Espera un momento e inténtalo de nuevo.",
        });
        return;
      }
    }

    setIsCapturing(true);
    setErrorMsg(null);

    try {
      const ctx = canvas.getContext("2d");

      const vw = video.videoWidth;
      const vh = video.videoHeight;

      // Reducción proporcional para no generar archivos gigantes
      let targetW = vw;
      let targetH = vh;
      const ratio = vw / vh;
      if (targetW > MAX_WIDTH) {
        targetW = MAX_WIDTH;
        targetH = Math.round(MAX_WIDTH / ratio);
      }
      if (targetH > MAX_HEIGHT) {
        targetH = MAX_HEIGHT;
        targetW = Math.round(MAX_HEIGHT * ratio);
      }

      canvas.width = targetW;
      canvas.height = targetH;

      // Espejo para selfie (si no lo quieres, comenta translate/scale y el restore)
      ctx.save();
      ctx.translate(targetW, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(video, 0, 0, targetW, targetH);
      ctx.restore();

      // Blob base de la captura
      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob(
          (b) =>
            b
              ? resolve(b)
              : reject(new Error("Error al generar la vista previa. Inténtalo de nuevo.")),
          "image/jpeg",
          0.92
        );
      });

      // Preparar preview inicial (rotación 0)
      setBaseBlob(blob);
      await ensureBaseBitmapFromBlob(blob);
      setRotation(0);
      await renderFromBaseAndUpdate(0);

      // Cerrar cámara y pasar a PREVIEW
      stopCamera();
      setView("preview");
    } catch (err) {
      console.error(err);
      showMessage({
        type: MESSAGE_TYPE.BAD,
        text: err.message || "No se pudo capturar la foto correctamente.",
      });
      setErrorMsg(err.message || "No se pudo capturar la foto correctamente.");
    } finally {
      setIsCapturing(false);
    }
  }, [MAX_HEIGHT, MAX_WIDTH, showMessage, stopCamera]);

  // --- Subir imagen final ---
  const handleUpload = useCallback(async () => {
    if (!lastBlob) {
      showMessage({
        type: MESSAGE_TYPE.BAD,
        text: "¡Ups! Debes tomar una foto antes de enviarla.",
      });
      setErrorMsg("Debes tomar una foto antes de enviarla.");
      return;
    }

    setIsUploading(true);
    setErrorMsg(null);

    try {
      const supportMessage = SUPPORT_MESSAGE_BY_ID[supportMessageSelected] || DEFAULT_SUPPORT_MESSAGE;

      // Payload con metadata requerida
      const payload = {
        mode: "binary",           // o "multipart" según tu DL
        blob: lastBlob,           // el archivo real
        caption: supportMessage,  // opcional
        source: isLiveMode ? "selfie_live" : "selfie_fan", // Live Barrio 9 o FanCam estándar
      };

      if (isLiveMode) {
        payload.fullName = leadData.fullName.trim();
        payload.email = leadData.email.trim();
        payload.phone = leadData.phone.replace(/\D/g, "");
        payload.acceptedTerms = leadData.acceptedTerms;
      }

      const response = await BLUploadSelfie(payload);

      if (response?.success) {
        // Fijar el cooldown (persistente)
        const ts = Date.now() + cooldownMs;
        setNextAllowedTs(ts);
        localStorage.setItem(COOLDOWN_STORAGE_KEY, String(ts));

        // Limpiar formulario para la siguiente participación
        setLeadData({ fullName: '', email: '', phone: '', acceptedTerms: false });
        setLeadErrors({});

        // Ir a ENVIADO
        setView("sent");

        // Limpieza mínima de estado crítico
        setSupportMessageSelected(0);
      } else {
        throw new Error(response?.message || "Error desconocido al subir.");
      }
    } catch (err) {
      console.error(err);
      showMessage({
        type: MESSAGE_TYPE.BAD,
        text:
          "Lo sentimos. Hubo un error al enviar tu foto. Inténtalo nuevamente más tarde.",
      });
      setErrorMsg(err.message || "Error al enviar la foto.");
    } finally {
      setIsUploading(false);
    }
  }, [lastBlob, supportMessageSelected, cooldownMs, showMessage, isLiveMode, leadData]);

  // --- UI ---
  return (
    <>
      <div className="container SelfieFan">
        {/* INTRO: Instrucciones + iniciar cámara */}
        {view === "intro" && (
          <div className="SelfieFan__Intro container">
            <img src="/img/page/adsl-five-bicolor.png" className="LogoADSLxZtudia" />
            <p className="SelfieFan__Fan__Title">Bienvenid@ Aficionad@</p>
            {isLiveMode ? (
              <p className="SelfieFan__Fan__Text--Center mb-0">¡Apoyemos a nuestro equipo! y participa por un regalo especial</p>
            ) : (
              <p className="SelfieFan__Fan__Text--Center mb-0">¡Apoyemos a nuestro equipo!</p>
            )}

            <ol className="SelfieFan__List">
              {isLiveMode && (
                <li>Rellena el formulario y compártenos tus datos de contacto para participar</li>
              )}
              <li>Tómate una selfie</li>
              {/* <li>Elige el mensaje de apoyo que más te guste</li> */}
              <li>Da clic en “Enviar mi foto”</li>
            </ol>

            <p className="SelfieFan__Fan__Text--Center mb-0">Tu foto podría aparecer en pantalla mostrando tu apoyo al equipo.</p>
            {isLiveMode && (
              <p className="SelfieFan__Fan__Text--Center">Además, participarás por un regalo especial.</p>
            )}

            {isLiveMode && (
              <hr />
            )}

            {isLiveMode && (
              <div className="card p-3 mb-3 border-0 mx-auto" style={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', borderRadius: '12px', maxWidth: '500px', width: '100%' }}>
                <p className="fw-bold text-center mb-3 text-dark">Ingresa tus datos para participar</p>
                
                <div className="mb-3 text-start">
                  <label className="form-label text-dark fw-semibold small mb-1">Nombre Completo *</label>
                  <input
                    type="text"
                    className={`form-control ${leadErrors.fullName ? 'is-invalid' : ''}`}
                    //placeholder="Ej. Juan Pérez"
                    value={leadData.fullName}
                    onChange={(e) => {
                      setLeadData(prev => ({ ...prev, fullName: e.target.value }));
                      if (leadErrors.fullName) setLeadErrors(prev => ({ ...prev, fullName: null }));
                    }}
                  />
                  {leadErrors.fullName && <div className="invalid-feedback">{leadErrors.fullName}</div>}
                </div>

                <div className="mb-3 text-start">
                  <label className="form-label text-dark fw-semibold small mb-1">Correo Electrónico *</label>
                  <input
                    type="email"
                    className={`form-control ${leadErrors.email ? 'is-invalid' : ''}`}
                    //placeholder="Ej. juan@correo.com"
                    value={leadData.email}
                    onChange={(e) => {
                      setLeadData(prev => ({ ...prev, email: e.target.value }));
                      if (leadErrors.email) setLeadErrors(prev => ({ ...prev, email: null }));
                    }}
                  />
                  {leadErrors.email && <div className="invalid-feedback">{leadErrors.email}</div>}
                </div>

                <div className="mb-3 text-start">
                  <label className="form-label text-dark fw-semibold small mb-1">Teléfono Celular (10 dígitos) *</label>
                  <input
                    type="tel"
                    maxLength={10}
                    className={`form-control ${leadErrors.phone ? 'is-invalid' : ''}`}
                    //placeholder="Ej. 4441234567"
                    value={leadData.phone}
                    onChange={(e) => {
                      const onlyNums = e.target.value.replace(/\D/g, '');
                      setLeadData(prev => ({ ...prev, phone: onlyNums }));
                      if (leadErrors.phone) setLeadErrors(prev => ({ ...prev, phone: null }));
                    }}
                  />
                  {leadErrors.phone && <div className="invalid-feedback">{leadErrors.phone}</div>}
                </div>

                <div className="form-check mb-2 text-start">
                  <input
                    type="checkbox"
                    className={`form-check-input ${leadErrors.acceptedTerms ? 'is-invalid' : ''}`}
                    id="acceptedTermsCheck"
                    checked={leadData.acceptedTerms}
                    onChange={(e) => {
                      setLeadData(prev => ({ ...prev, acceptedTerms: e.target.checked }));
                      if (leadErrors.acceptedTerms) setLeadErrors(prev => ({ ...prev, acceptedTerms: null }));
                    }}
                  />
                  <label className="form-check-label text-dark small" htmlFor="acceptedTermsCheck">
                    Acepto los{' '}
                    <button
                      type="button"
                      className="btn btn-link p-0 text-decoration-underline text-danger small align-baseline fw-bold"
                      onClick={(e) => { e.preventDefault(); setShowTermsModal(true); }}
                    >
                      Términos y Condiciones
                    </button>
                    {' '}y el{' '}
                    <button
                      type="button"
                      className="btn btn-link p-0 text-decoration-underline text-danger small align-baseline fw-bold"
                      onClick={(e) => { e.preventDefault(); setShowPrivacyModal(true); }}
                    >
                      Aviso de Privacidad
                    </button>
                    .
                  </label>
                  {leadErrors.acceptedTerms && <div className="invalid-feedback d-block">{leadErrors.acceptedTerms}</div>}
                </div>
              </div>
            )}

            <div className="SelfieFan__Intro__Actions">
              <button
                className="Btn__Adsl--Red"
                onClick={handleStartCameraWithValidation}
                disabled={isStarting || !canStartNew}
                title={!canStartNew ? `Puedes tomar otra en ${msToMinSec(remainingMs)}` : ""}
              >
                {isStarting
                  ? "Abriendo cámara..."
                  : canStartNew
                  ? (
                    isLiveMode ? (
                      <>
                        <span className="bi bi-camera" /> Iniciar cámara
                      </>
                    ) : (
                      <>
                        <span className="bi bi-camera" /> Iniciar cámara
                      </>
                    )
                  )
                  : `Puedes tomar otra en ${msToMinSec(remainingMs)}`}
              </button>
            </div>
          </div>
        )}

        {/* CÁMARA: overlay pantalla completa + botón flotante "Tomar foto" */}
        {view === "camera" && (
          <div className="SelfieFan__Overlay">
            <div className="SelfieFan__Overlay__VideoWrapper">
              <video
                ref={videoRef}
                className="SelfieFan__Overlay__Video"
                autoPlay
                playsInline
                muted
              />
              <button
                className="SelfieFan__Overlay__CaptureBtn Btn__Adsl--Red"
                onClick={handleCapture}
                disabled={isCapturing || !isVideoReady}
              >
                {isCapturing
                  ? "Capturando..."
                  : isVideoReady
                  ? (
                    <>
                      <span className="bi bi-camera" /> Tomar foto
                    </>
                  )
                  : "Preparando cámara..."}
              </button>
            </div>
          </div>
        )}

        {/* Canvas oculto para procesar imagen */}
        <canvas ref={canvasRef} className="d-none" />

        {/* PREVIEW: imagen + girar + elegir mensaje + enviar */}
        {view === "preview" && (
          <div className="SelfieFan__Preview container">
            <img src="/img/page/adsl-five-bicolor.png" className="LogoADSLxZtudia" />
            <p className="text-center">Vista previa:</p>

            {/* Acciones principales de preview */}
            <div className="SelfieFan__Preview__RotateActions">
              <button
                type="button"
                className="Btn__Adsl--RedOutline SelfieFan__Preview__RotateActions__RAButton"
                disabled={!baseBlob || isCapturing}
                onClick={async () => {
                  const newRot = (rotation + 90) % 360;
                  setRotation(newRot);
                  await renderFromBaseAndUpdate(newRot);
                }}
              >
                <i className="bi bi-arrow-clockwise" /> Girar
              </button>

              <button
                className="Btn__Adsl--RedOutline SelfieFan__Preview__RotateActions__RAButton"
                onClick={async () => {
                  // Limpiar preview y volver a cámara (no aplica cooldown hasta que se envíe)
                  setPreviewUrl(null);
                  if (previewUrlRef.current) {
                    URL.revokeObjectURL(previewUrlRef.current);
                    previewUrlRef.current = null;
                  }
                  setBaseBlob(null);
                  setLastBlob(null);
                  setRotation(0);
                  await startCamera();
                }}
                disabled={isStarting}
              >
                <i className="bi bi-camera" /> Tomar otra
              </button>
            </div>

            {previewUrl && (
              <img
                src={previewUrl}
                alt="Tu foto aparecerá aquí"
                className="SelfieFan__Preview__Image"
              />
            )}

            {/* Enviar */}
            <div className="SelfieFan__Preview__Send">
              <button
                onClick={handleUpload}
                className="Btn__Adsl--Red"
                disabled={!lastBlob || isUploading}
              >
                {isUploading ? "Subiendo foto..." : "Enviar mi foto"}
              </button>
            </div>

            {errorMsg && (
              <p className="text-center mt-4" style={{ color: "crimson" }}>
                <strong>Error: </strong> {errorMsg}
              </p>
            )}
          </div>
        )}

        {/* ENVIADO: confirmación + botón bloqueado hasta fin del cooldown */}
        {view === "sent" && (
          <div className="SelfieFan__Sent container">
            <img src="/img/page/adsl-five-bicolor.png" className="LogoADSLxZtudia" />
            <p className="fs-1 mb-0 text-success">
              <i className="bi bi-check-circle-fill" />
            </p>
            <p className="SelfieFan__Sent__Title">¡Gracias por tu apoyo!</p>
            <p className="SelfieFan__Fan__Text--Center">
              Tu foto ha sido enviada correctamente
            </p>

            <div className="SelfieFan__Sent__Actions">
              <button
                className="Btn__Adsl--RedOutline mx-auto"
                onClick={() => {
                  if (!canStartNew) return; // seguridad extra

                  // Reiniciar flujo (estado de imagen)
                  setPreviewUrl(null);
                  if (previewUrlRef.current) {
                    URL.revokeObjectURL(previewUrlRef.current);
                    previewUrlRef.current = null;
                  }
                  setBaseBlob(null);
                  setLastBlob(null);
                  setRotation(0);
                  setErrorMsg(null);
                  setSupportMessageSelected(0);
                  setLeadData({ fullName: '', email: '', phone: '', acceptedTerms: false });
                  setLeadErrors({});

                  // Volver a intro
                  setView("intro");
                }}
                disabled={!canStartNew}
                title={!canStartNew ? `Puedes tomar otra foto en ${msToMinSec(remainingMs)}` : ""}
              >
                {canStartNew ? "Tomar otra foto" : `Puedes tomar otra foto en ${msToMinSec(remainingMs)}`}
              </button>
            </div>

            <hr />

            <a href='https://interaktia.com/?utm_source=selfiefan_web&utm_medium=banner&utm_campaign=fdm_adsl' target='_blank' className='SelfieFan__Sent__Banner'>
              <img src='/img/advertising/fdm/banner/five-digital-minds-agradecimiento.gif' alt='Certifícate con Ztudia' className='d-block m-auto SelfieFan__Sent__Banner__Image' />
            </a>
            <a href='https://interaktia.com/?utm_source=selfiefan_web&utm_medium=banner&utm_campaign=fdm_adsl' target='_blank' className='Btn__Adsl--RedOutline m-auto text-decoration-none text-center d-block'>Contactar ahora</a>

            <hr />

            <p className='text-center mb-0'>¿Quieres conocer más de esta tecnología?</p>
            <p className='text-center mb-0'>Interaktia está listo para ti</p>
            <a href='https://interaktia.com/?utm_source=selfiefan_web&utm_medium=banner_text&utm_campaign=fdm_adsl' target='_blank' className='Btn__Adsl--RedOutline m-auto text-decoration-none text-center'>Contactar ahora</a>
          </div>
        )}
      </div>

      {/* Modales Legales Flotantes */}
      {showTermsModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 10500 }}>
          <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content text-dark">
              <div className="modal-header">
                <h5 className="modal-title fw-bold">Términos y Condiciones</h5>
                <button type="button" className="btn-close" onClick={() => setShowTermsModal(false)}></button>
              </div>
              <div className="modal-body text-start" style={{ whiteSpace: 'pre-line', fontSize: '0.88rem' }}>
                {LIVE_TERMS_TEXT}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowTermsModal(false)}>Cerrar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showPrivacyModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 10500 }}>
          <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content text-dark">
              <div className="modal-header">
                <h5 className="modal-title fw-bold">Aviso de Privacidad</h5>
                <button type="button" className="btn-close" onClick={() => setShowPrivacyModal(false)}></button>
              </div>
              <div className="modal-body text-start" style={{ whiteSpace: 'pre-line', fontSize: '0.88rem' }}>
                {LIVE_PRIVACY_TEXT}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowPrivacyModal(false)}>Cerrar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <footer className="text-center mt-3 FooterCredits">
        Hecho con ♥ por <a href='https://interaktia.com/?utm_source=selfiefan_web&utm_medium=footer&utm_campaign=fdm_adsl' target='_blank' className='text-dark'>Interaktia</a> | 2026
      </footer>
    </>
  );
};

export { SelfieFan };